import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Pagination,
  InputAdornment,
  Chip
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
  FilterList as FilterIcon,
  SmokingRooms as SmokeIcon,
  MenuBook as JournalIcon,
} from '@mui/icons-material'
import { useActiveSmokingSession } from '@hooks/useActiveSmokingSession'
import { isActiveSmokingSession } from '@utils/smokingSessionState'
import { trackFeature } from '@utils/trackFeature'
import { useGetSessionsQuery, useDeleteSessionMutation, useGetSessionStatsQuery } from '@store/api/sessionApi'
import { ExportButton } from '../shared/components/ExportButton'
import { useAppSelector, useAppDispatch } from '@store/hooks'
import { useOfflineCollection } from '@hooks/useOfflineCollection'
import { filterOfflineSessions, paginateSessions } from '@/lib/offline/filterSessions'
import { offlineDeleteSession } from '@services/offlineWriteService'
import SessionCard from '@components/sessions/SessionCard'
import ActiveSessionCard from '@components/sessions/ActiveSessionCard'
import SessionForm from '@components/sessions/SessionForm'
import { SmokingSession } from '@store/api/sessionApi'
import { formatAverageRatingDisplay } from '@utils/statDisplay'

type DateRangePreset = 'all' | 'week' | 'month' | 'year' | 'custom'

const getDateRange = (preset: DateRangePreset): { dateFrom?: string; dateTo?: string } => {
  const now = new Date()
  const toISO = (d: Date) => d.toISOString().split('T')[0]
  if (preset === 'week') {
    const from = new Date(now)
    from.setDate(from.getDate() - 7)
    return { dateFrom: toISO(from), dateTo: toISO(now) }
  }
  if (preset === 'month') {
    const from = new Date(now)
    from.setMonth(from.getMonth() - 1)
    return { dateFrom: toISO(from), dateTo: toISO(now) }
  }
  if (preset === 'year') {
    const from = new Date(now)
    from.setFullYear(from.getFullYear() - 1)
    return { dateFrom: toISO(from), dateTo: toISO(now) }
  }
  return {}
}

const SmokingJournal: React.FC = () => {
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const { isOnline } = useAppSelector((state) => state.offline)
  const { offlineSessions, isLoadingOffline, cacheSessions } = useOfflineCollection()
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'smokedDate' | 'rating' | 'createdAt'>('smokedDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showForm, setShowForm] = useState(false)
  const [editingSession, setEditingSession] = useState<SmokingSession | null>(null)
  const [showStats, setShowStats] = useState(false)
  const [datePreset, setDatePreset] = useState<DateRangePreset>('all')
  const [customDateFrom, setCustomDateFrom] = useState('')
  const [customDateTo, setCustomDateTo] = useState('')
  const [journalExpanded, setJournalExpanded] = useState(false)

  const { activeSession, resumableDraft } = useActiveSmokingSession()

  const dateRange = datePreset === 'custom'
    ? { dateFrom: customDateFrom || undefined, dateTo: customDateTo || undefined }
    : getDateRange(datePreset)

  const { data: sessionsData, isLoading: sessionsLoading, error: sessionsError } = useGetSessionsQuery({
    page,
    limit: 12,
    sortBy,
    sortOrder,
    includeDrafts: true,
    ...(searchTerm && { search: searchTerm }),
    ...dateRange
  }, {
    skip: !isAuthenticated || !isOnline
  })

  const { data: fullSessionsCache } = useGetSessionsQuery(
    { page: 1, limit: 5000, sortBy: 'smokedDate', sortOrder: 'desc', includeDrafts: true },
    { skip: !isAuthenticated || !isOnline },
  )

  useEffect(() => {
    if (isOnline && fullSessionsCache?.sessions) {
      cacheSessions(fullSessionsCache.sessions)
    }
  }, [fullSessionsCache, isOnline, cacheSessions])

  const offlinePage = useMemo(() => {
    if (isOnline) return null
    const filtered = filterOfflineSessions(offlineSessions, {
      search: searchTerm || undefined,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
      sortBy,
      sortOrder,
    })
    return paginateSessions(filtered, page, 12)
  }, [isOnline, offlineSessions, searchTerm, dateRange, sortBy, sortOrder, page])

  const sessions = isOnline ? (sessionsData?.sessions ?? []) : (offlinePage?.sessions ?? [])
  const sessionsTotalPages = isOnline ? (sessionsData?.totalPages ?? 1) : (offlinePage?.totalPages ?? 1)
  const isSessionsLoading = isOnline ? sessionsLoading : isLoadingOffline

  const { data: statsData } = useGetSessionStatsQuery({}, {
    skip: !isAuthenticated || !isOnline
  })

  const [deleteSession] = useDeleteSessionMutation()

  const handleCreateSession = () => {
    setEditingSession(null)
    setShowForm(true)
  }

  const handleStartSessionCta = () => {
    trackFeature('cta_smoke_start', { source: 'smoke_tab' })
    handleCreateSession()
  }

  const handleResumeDraft = () => {
    if (!resumableDraft) return
    trackFeature('cta_smoke_start', { source: 'smoke_tab' })
    setEditingSession(resumableDraft)
    setShowForm(true)
  }

  const handleEditSession = (session: SmokingSession) => {
    setEditingSession(session)
    setShowForm(true)
  }

  const handleDeleteSession = async (sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        if (!isOnline) {
          await offlineDeleteSession(dispatch, sessionId)
          return
        }
        await deleteSession(sessionId).unwrap()
      } catch (error) {
        console.error('Failed to delete session:', error)
      }
    }
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingSession(null)
  }

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
    setPage(1)
  }

  const handleSortChange = (field: 'smokedDate' | 'rating' | 'createdAt') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
    setPage(1)
  }

  const handleDatePreset = (preset: DateRangePreset) => {
    setDatePreset(preset)
    setPage(1)
  }

  const hasActiveFilters = searchTerm || datePreset !== 'all'

  const handleClearFilters = () => {
    setSearchTerm('')
    setDatePreset('all')
    setCustomDateFrom('')
    setCustomDateTo('')
    setPage(1)
  }

  if (!isAuthenticated) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Smoking Journal
        </Typography>
        <Alert severity="info">
          Please log in to view your smoking journal.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" component="h1">
          Smoke
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            startIcon={<JournalIcon />}
            onClick={() => setJournalExpanded((v) => !v)}
            sx={{ minHeight: 44 }}
          >
            {journalExpanded ? 'Hide journal' : 'Past sessions'}
          </Button>
        </Stack>
      </Box>

      {activeSession && (
        <Box mb={3} data-testid="smoke-live-session">
          <ActiveSessionCard session={activeSession} />
        </Box>
      )}

      {!activeSession && resumableDraft && (
        <Card sx={{ mb: 3 }} data-testid="smoke-resume-draft">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resume your draft
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {resumableDraft.cigar.cigarLine.brand.name} {resumableDraft.cigar.cigarLine.name} —{' '}
              {resumableDraft.cigar.vitola.name}
            </Typography>
            <Button variant="contained" size="large" fullWidth startIcon={<SmokeIcon />} onClick={handleResumeDraft} sx={{ minHeight: 48 }}>
              Continue session
            </Button>
          </CardContent>
        </Card>
      )}

      {!activeSession && !resumableDraft && (
        <Card sx={{ mb: 3 }} data-testid="smoke-start-idle">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Ready to smoke?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Start a live session to track time and tasting notes — your past sessions stay in the journal.
            </Typography>
            <Button variant="contained" size="large" fullWidth startIcon={<SmokeIcon />} onClick={handleStartSessionCta} sx={{ minHeight: 48 }}>
              Start session
            </Button>
          </CardContent>
        </Card>
      )}

      {journalExpanded && (
        <Box mb={2} display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TimelineIcon />}
            onClick={() => setShowStats(!showStats)}
          >
            {showStats ? 'Hide Stats' : 'Show Stats'}
          </Button>
          <ExportButton dataType="sessions" />
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleCreateSession}>
            Record completed session
          </Button>
        </Box>
      )}

      {journalExpanded && showStats && statsData && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <TimelineIcon color="primary" />
                  <Typography variant="h6">{statsData.totalSessions}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Sessions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <StarIcon color="primary" />
                  <Typography variant="h6">
                    {formatAverageRatingDisplay(statsData.averageRating)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Average Rating
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarIcon color="primary" />
                  <Typography variant="h6">
                    {Math.round(statsData.totalSmokingTime / 60)}h
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Smoking Time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <TimelineIcon color="primary" />
                  <Typography variant="h6">
                    {Math.round(statsData.averageSessionDuration)}m
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Session Duration
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {journalExpanded && (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Search by cigar name or notes..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value as typeof sortBy)}
                >
                  <MenuItem value="smokedDate">Date</MenuItem>
                  <MenuItem value="rating">Rating</MenuItem>
                  <MenuItem value="createdAt">Created</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                >
                  <MenuItem value="desc">Newest First</MenuItem>
                  <MenuItem value="asc">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {hasActiveFilters && (
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  startIcon={<FilterIcon />}
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              </Grid>
            )}
          </Grid>

          {/* Date Range Filters */}
          <Box mt={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(['all', 'week', 'month', 'year', 'custom'] as DateRangePreset[]).map((preset) => (
                <Chip
                  key={preset}
                  label={preset === 'all' ? 'All Time' : preset === 'week' ? 'Last 7 Days' : preset === 'month' ? 'Last Month' : preset === 'year' ? 'Last Year' : 'Custom Range'}
                  onClick={() => handleDatePreset(preset)}
                  color={datePreset === preset ? 'primary' : 'default'}
                  variant={datePreset === preset ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
          </Box>

          {/* Custom Date Range Inputs */}
          {datePreset === 'custom' && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => { setCustomDateFrom(e.target.value); setPage(1) }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={customDateTo}
                  onChange={(e) => { setCustomDateTo(e.target.value); setPage(1) }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
      )}

      {journalExpanded && (isSessionsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : sessionsError && isOnline ? (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load sessions. Please try again.
          </Alert>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Box>
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No sessions found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {hasActiveFilters
                  ? 'No sessions match your current filters. Try adjusting your search or date range.'
                  : 'No completed sessions in your journal yet.'}
              </Typography>
              {hasActiveFilters ? (
                <Button variant="outlined" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              ) : (
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreateSession}>
                  Record completed session
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {sessions
              .filter((s) => !isActiveSmokingSession(s) && s.id !== activeSession?.id)
              .map((session) => (
                <Grid item xs={12} sm={6} md={4} key={session.id}>
                  <SessionCard
                    session={session}
                    onEdit={handleEditSession}
                    onDelete={handleDeleteSession}
                  />
                </Grid>
              ))}
          </Grid>

          {sessionsTotalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={sessionsTotalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      ))}

      {/* Session Form Dialog */}
      <SessionForm
        open={showForm}
        onClose={handleCloseForm}
        session={editingSession}
      />
    </Box>
  )
}

export default SmokingJournal
